import {
  AfterContentChecked,
  ChangeDetectorRef,
  Injectable,
  OnChanges,
} from '@angular/core';

/**
 * Provider for initial HookState value
 */
export interface HookInitial<T> {
  (): T;
}

/**
 * Clean up HookState
 */
export interface HookCleanup<T> {
  (value: T): void;
}

/**
 * Hook state
 */
export interface HookState<T> {
  get(): T;

  set(value: T): void;
}

/**
 * Hook state manager
 */
export interface HookStateManager {
  invalidate(): void;
  nextState<T>(
    initial: HookInitial<T>,
    cleanup?: HookCleanup<T>,
  ): HookStateImpl<T>;
  cleanup(): void;
}

export class HookStateImpl<T> implements HookState<T> {
  constructor(
    private _value: T,
    private _cleanup: (value: T) => void,
  ) {}

  cleanup() {
    this._cleanup(this._value);
  }

  get(): T {
    return this._value;
  }

  set(value: T) {
    this._value = value;
  }
}

/**
 * Current HookStateManager
 */
let stateManager: HookStateManager | undefined;

/**
 * Get current HookStateManager
 */
export function hookStateManager(): HookStateManager {
  if (!stateManager) {
    throw new Error('Hook called outside render');
  }
  return stateManager;
}

class HookStateManagerImpl implements HookStateManager {
  constructor(readonly invalidate: () => void) {}

  private readonly _states: HookStateImpl<any>[] = [];

  private _i = 0;

  cleanup(): void {
    for (const state of this._states) {
      state.cleanup();
    }
  }

  nextState<T>(
    initial: () => T,
    cleanup: (value: T) => void = () => {},
  ): HookStateImpl<T> {
    if (this._states.length <= this._i) {
      this._states.push(new HookStateImpl(initial(), cleanup));
    }
    const i = this._i++;
    return this._states[i];
  }

  reset() {
    this._i = 0;
  }
}

/**
 * Bindings
 */
export type HookDirectiveBindings = any;

/**
 * Renderer
 */
export interface HookDirectiveRenderer {
  (): HookDirectiveBindings;
}

const rendererPropertyName = '_';

/**
 * Get the renderer from the prototype
 */
function componentRenderer(object: HookDirective): HookDirectiveRenderer {
  const { constructor } = object;
  const descriptor = Object.getOwnPropertyDescriptor(
    constructor.prototype,
    rendererPropertyName,
  );
  if (!descriptor) {
    throw new Error(`${constructor} has no property ${rendererPropertyName}`);
  }
  const { get, value } = descriptor;
  return get ? get : () => value;
}

/**
 * Base implementation of hook-base directive or component.
 * Subclasses must inject ChangeDetectorRef and pass it to this base class.
 */
@Injectable()
export abstract class HookDirective implements AfterContentChecked, OnChanges {
  constructor(private readonly _changeDetector: ChangeDetectorRef) {
    // overwrite _ property with cached value
    Object.defineProperty(this, rendererPropertyName, {
      get: () => {
        this._refresh();
        return this._cache;
      },
    });
  }

  /** Cache bindings */
  private _cache: HookDirectiveBindings;

  /** Dirty */
  private _dirty = true;

  /** Renderer */
  private readonly _renderer = componentRenderer(this);

  /** Binding property */
  abstract _: HookDirectiveBindings;

  /** State manager for this component */
  private readonly _stateManager = new HookStateManagerImpl(() =>
    this._invalidate(),
  );

  /** Invalid cache */
  private _invalidate() {
    if (stateManager) {
      return;
    }
    this._dirty = true;
    this._changeDetector.markForCheck();
  }

  ngAfterContentChecked() {
    // ensure side effects get run, even if there is not template
    this._refresh();
  }

  ngOnChanges() {
    this._dirty = true;
  }

  ngOnDestroy() {
    this._stateManager.cleanup();
  }

  /** Run checks */
  _refresh() {
    if (!this._dirty) {
      return;
    }

    if (stateManager !== undefined) {
      throw new Error('State manager already set');
    }
    this._stateManager.reset();
    stateManager = this._stateManager;
    try {
      this._cache = this._renderer();
    } finally {
      stateManager = undefined;
      this._dirty = false;
    }
  }
}
